import { http } from "../../utils/config";

const cnfAPI = {
  // Get all with pagination and search
  getCnfs: (params = {}) => {
    const {
      page = 1,
      per_page = 15,
      search = '',
      status = '',
      sort_by = 'created_at',
      sort_order = 'desc'
    } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: per_page.toString(),
      sort_by,
      sort_order,
      ...(search && { search }),
      ...(status !== '' && { status })
    });

    return http.get(`/api/v1/invoices/cnf?${queryParams}`);
  },

  getCnfAll: (params = {}) => {
    return http.get(`/api/v1/invoices/cnf/all`, { params });
  },

  // Get by ID
  getCnfById: (id) => {
    return http.get(`/api/v1/invoices/cnf/${id}`);
  },

  // Create new
  createCnf: (cnfData) => {
    return http.post('/api/v1/invoices/cnf', cnfData);
  },

  // Update
  updateCnf: (id, cnfData) => {
    return http.put(`/api/v1/invoices/cnf/${id}`, cnfData);
  },

  // Delete
  deleteCnf: (id) => {
    return http.delete(`/api/v1/invoices/cnf/${id}`);
  },

};

// Helper functions for handling API responses
export const cnfHelpers = {
  // Extract data from API response
  extractCnfData: (response) => {
    if (response.data && response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data?.message || 'Unknown error occurred');
  },

  // Extract paginated list from API response
  extractCnfList: (response) => {
    if (response.data && response.data.success) {
      const data = response.data.data;
      return {
        cnfs: data.data || [],
        pagination: {
          current: data.current_page,
          pageSize: data.per_page,
          total: data.total,
          from: data.from,
          to: data.to,
          lastPage: data.last_page,
          nextPageUrl: data.next_page_url,
          prevPageUrl: data.prev_page_url
        }
      };
    }
    throw new Error(response.data?.message || 'Unknown error occurred');
  },

  // Format data for form submission
  formatCnfData: (formData) => {
    return {
      name: formData.name?.trim(),
      description: formData.description?.trim(),
      status: formData.status ?? true,
    };
  },

  // Validate data before submission
  validateCnfData: (cnfData) => {
    const errors = {};

    if (!cnfData.name?.trim()) {
      errors.name = 'Tên khách hàng không được để trống';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }
};

// Error handling wrapper
export const withErrorHandling = (apiFunction) => {
  return async (...args) => {
    try {
      const response = await apiFunction(...args);
      return response;
    } catch (error) {
      console.error('API Error:', error);
      
      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const { status, data } = error.response;
        switch (status) {
          case 400:
            throw new Error(data.message || 'Dữ liệu không hợp lệ');
          case 401:
            throw new Error('Bạn cần đăng nhập để thực hiện thao tác này');
          case 403:
            throw new Error('Bạn không có quyền thực hiện thao tác này');
          case 404:
            throw new Error('Không tìm thấy khách hàng');
          case 422:
            throw new Error(data.message || 'Dữ liệu không hợp lệ');
          case 500:
            throw new Error(data.message || "Lỗi server, vui lòng thử lại sau");
          default:
            throw new Error(data.message || 'Đã có lỗi xảy ra');
        }
      } else if (error.request) {
        // Network error
        throw new Error('Không thể kết nối đến server');
      } else {
        // Other error
        throw new Error(error.message || 'Đã có lỗi xảy ra');
      }
    }
  };
};

// Wrapped API functions with error handling
export const cnfAPIWithErrorHandling = {
  getCnfs: withErrorHandling(cnfAPI.getCnfs),
  getCnfAll: withErrorHandling(cnfAPI.getCnfAll),
  getCnfById: withErrorHandling(cnfAPI.getCnfById),
  createCnf: withErrorHandling(cnfAPI.createCnf),
  updateCnf: withErrorHandling(cnfAPI.updateCnf),
  deleteCnf: withErrorHandling(cnfAPI.deleteCnf),
};

export default cnfAPI;